import * as Sentry from '@sentry/react';
import { useState, type ReactElement } from 'react';

import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useGithubConnect,
  useGithubConnectManual,
  useGithubDisconnect,
  useGithubStatus,
  useGithubSync,
  useGithubSyncStatus,
} from '@/hooks/api/useGithub';
import { useToast } from '@/hooks/use-toast';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';
import { DisconnectDialog } from '@/pages/organization/components/DisconnectDialog';
import { ManualConnectDialog } from '@/pages/organization/components/ManualConnectDialog';

import {
  GithubIntegrationCard,
  type GithubCardStatus,
} from './GithubIntegrationCard';
import { GithubProvider } from './providers';
import {
  useIntegrationSyncHandler,
  useSettledEmptySentryEffect,
} from './useIntegrationSync';

interface GithubIntegrationProps {
  access: RenderableIntegrationAccess;
}

export function GithubIntegration({
  access,
}: GithubIntegrationProps): ReactElement {
  if (access.canRender) return <GithubIntegrationInner access={access} />;
  return (
    <GithubIntegrationCard
      status={{
        enabled: false,
        connectionStatus: 'none',
        connected: false,
      }}
      cardStatus="not_connected"
      isLoading
    />
  );
}

GithubIntegration.displayName = 'GithubIntegration';

function GithubIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement | null {
  const { organizationId, isLoading: isAccessLoading } = access;

  const githubQuery = useGithubStatus(organizationId);
  const githubSyncQuery = useGithubSyncStatus(organizationId, {
    enabled: githubQuery.data?.connected === true,
  });
  const connectMutation = useGithubConnect(organizationId);
  const connectManualMutation = useGithubConnectManual(organizationId);
  const disconnectMutation = useGithubDisconnect(organizationId);
  const syncMutation = useGithubSync(organizationId);
  const { toast } = useToast();

  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showManualConnectDialog, setShowManualConnectDialog] = useState(false);
  const [manualConnectError, setManualConnectError] = useState<
    string | undefined
  >();

  const handleConnect = async (): Promise<void> => {
    try {
      const result = await connectMutation.mutateAsync();
      if ('enabled' in result && result.enabled === false) {
        toast({
          title: 'Feature not available',
          description: 'GitHub integration is not currently enabled.',
          variant: 'destructive',
        });
        return;
      }
      let url: URL;
      try {
        url = new URL(result.installUrl);
      } catch (parseErr) {
        Sentry.captureException(parseErr, {
          tags: { error_type: 'github_connect' },
          extra: { installUrl: result.installUrl },
        });
        toast({
          title: 'Connection failed',
          description: 'Received an invalid install URL. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      if (url.protocol !== 'https:' || url.host !== 'github.com') {
        Sentry.captureException(
          new Error('Unexpected installUrl host/scheme'),
          {
            tags: { error_type: 'github_connect' },
            extra: { installUrl: result.installUrl },
          }
        );
        toast({
          title: 'Connection failed',
          description: 'Received an invalid install URL. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      window.location.href = url.toString();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'github_connect' },
        extra: { organizationId },
      });
      toast({
        title: 'Connection failed',
        description: 'Failed to start GitHub connection. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const openManualConnect = (): void => {
    setManualConnectError(undefined);
    setShowManualConnectDialog(true);
  };

  const handleManualConnectSubmit = async (
    installationId: string
  ): Promise<void> => {
    setManualConnectError(undefined);
    try {
      const result = await connectManualMutation.mutateAsync(installationId);
      if ('enabled' in result && result.enabled === false) {
        setManualConnectError('GitHub integration is not currently enabled.');
        return;
      }
      if ('success' in result && result.success) {
        setShowManualConnectDialog(false);
        toast({
          title: 'GitHub connected',
          description: `Successfully connected to ${result.githubOrgName}. Syncing members…`,
        });
        return;
      }
      setManualConnectError(
        'Unexpected response from server. Please try again.'
      );
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'github_manual_connect' },
        extra: { organizationId },
      });
      setManualConnectError(getErrorMessage(err));
    }
  };

  const handleDisconnect = (): void => setShowDisconnectDialog(true);
  const closeDisconnect = (): void => setShowDisconnectDialog(false);

  const confirmDisconnect = async (): Promise<void> => {
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectDialog(false);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: 'github_disconnect' },
        extra: { organizationId },
      });
      toast({
        title: 'Disconnect failed',
        description: 'Failed to disconnect GitHub. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSync = useIntegrationSyncHandler({
    provider: GithubProvider,
    organizationId,
    syncMutation,
  });

  const cardStatus: GithubCardStatus = githubQuery.data?.connected
    ? 'connected'
    : githubQuery.error
      ? 'error'
      : 'not_connected';

  const errorMessage = githubQuery.error
    ? githubQuery.error instanceof Error
      ? githubQuery.error.message
      : 'An unexpected error occurred. Please try again.'
    : undefined;

  const isLoading = isAccessLoading || githubQuery.isLoading;
  const isSyncing =
    syncMutation.isPending || githubSyncQuery.data?.isRunning === true;

  const isSettledEmpty =
    !githubQuery.data &&
    !githubQuery.error &&
    !isAccessLoading &&
    !githubQuery.isLoading;

  useSettledEmptySentryEffect({
    isSettledEmpty,
    provider: GithubProvider,
    organizationId,
  });

  if (isSettledEmpty) return null;

  const status = githubQuery.data ?? {
    enabled: false,
    connectionStatus: 'none' as const,
    connected: false,
  };

  return (
    <>
      <GithubIntegrationCard
        status={status}
        syncStatus={githubSyncQuery.data}
        cardStatus={cardStatus}
        error={errorMessage}
        isLoading={isLoading}
        isSyncing={isSyncing}
        onConnect={handleConnect}
        onManualConnect={openManualConnect}
        onDisconnect={handleDisconnect}
        onSync={handleSync}
        readOnly={!access.canWrite}
        connectIsPending={connectMutation.isPending}
        disconnectIsPending={disconnectMutation.isPending}
        connectTooltip="Install the GitHub App on your organization"
        syncNote="Adding a user in GitHub will automatically add them to Kirin with an Imported status in the Members tab."
      />
      {showDisconnectDialog && (
        <DisconnectDialog
          provider={GithubProvider}
          isOpen={showDisconnectDialog}
          onClose={closeDisconnect}
          onConfirm={confirmDisconnect}
          isLoading={disconnectMutation.isPending}
          readOnly={!access.canWrite}
        />
      )}
      {showManualConnectDialog && (
        <ManualConnectDialog
          key={organizationId}
          provider={GithubProvider}
          organizationId={organizationId}
          isOpen={showManualConnectDialog}
          onClose={() => setShowManualConnectDialog(false)}
          onConfirm={handleManualConnectSubmit}
          isLoading={connectManualMutation.isPending}
          error={manualConnectError}
          readOnly={!access.canWrite}
        />
      )}
    </>
  );
}

GithubIntegrationInner.displayName = 'GithubIntegrationInner';
